/**
 * SystemDox CaptureHost (ADR-022 item 2).
 *
 * Widens the #540 RefineAdapter into the full adapter bundle the shared
 * capture package (@puglieseweb/shared-react-capture, ADR-022 item 3)
 * consumes: refine + validate (from refineAdapter), plus persistence
 * (specsApi, spec+docType model), gap analysis (aiApi), navigation, and the
 * doc-type/template taxonomy. Tenancy (TENANT#) stays invisible — it rides
 * the auth token inside specsApi/aiApi; the host passes no tenant id.
 *
 * The CaptureHost interface is mirrored here because the shared package is
 * not yet published to GitHub Packages. When it is, replace this interface
 * with `import type { CaptureHost } from '@puglieseweb/shared-react-capture'`
 * and delete the local copy — the impl below already conforms.
 */

import { specsApi, type DocType, type SpecTemplate } from '@/services/specsApi';
import { aiApi } from '@/services/aiApi';
import {
  systemdoxRefineAdapter,
  validateRefinedDocument,
  type RefineRequest,
  type RefinedDocument,
  type ValidationIssue,
} from '@/services/refineAdapter';
import { tenantUrl } from '@/utils/subdomain';

export interface DocPatch {
  name?: string;
  proposalContent?: string;
  taskContent?: string;
  status?: string;
  requirements?: { name: string; content: string }[];
}

export interface CreateDocInput {
  /** Omit to have the backend auto-generate a title from `notes`. */
  name?: string;
  /** Falls back to the chosen template's docType when omitted. */
  docType?: string;
  /** Platform template key (e.g. 'adr'). */
  template?: string;
  /** Custom (tenant) template id. Wins over `template`. */
  templateId?: string;
  /** Captured notes — used server-side to derive the title when `name` is omitted. */
  notes?: string;
}

export interface DocSummary {
  id: string;
  name: string;
  status?: string;
  updatedAt?: string;
}

export interface CaptureGap {
  area: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
}

export interface GapAnalysisResult {
  gaps: CaptureGap[];
  summary: string;
}

export interface TemplateOption {
  key: string;
  title: string;
  description: string;
}

export interface DocTypeOption {
  key: string;
  label: string;
  templates: TemplateOption[];
  aiDraft?: boolean;
}

export interface DocTypeConfig {
  types: DocTypeOption[];
}

/** The contract the shared capture UI depends on (mirrors @noteble/capture). */
export interface CaptureHost {
  refine(request: RefineRequest): Promise<RefinedDocument>;
  validate(doc: RefinedDocument, docType: string): ValidationIssue[];
  createDoc(input: CreateDocInput): Promise<{ id: string; docType: string }>;
  getDoc(id: string): Promise<{ proposalContent: string; name?: string; gitRepo?: string; gitPath?: string }>;
  updateDoc(id: string, patch: DocPatch): Promise<void>;
  /** Commit a freshly-filled draft to git as `status: draft` so it is versioned
   *  and searchable from creation (ADR-033). Graceful: a no-repo tenant is a
   *  no-op. Optional so an embedding host without git can omit it. */
  commitDoc?(id: string): Promise<void>;
  listRecent(docType: string): Promise<DocSummary[]>;
  analyzeGaps(): Promise<GapAnalysisResult>;
  onOpenDocument(id: string, docType: string): void;
  docTypeConfig: DocTypeConfig;
}

/**
 * SystemDox's requirements + SDLC template taxonomy (ADR-022 Revision 2 — the
 * one deliberate divergence from Noteble's general document types).
 *   requirement → BDD, PRD
 *   design      → ADR, design-doc, RFC, test-plan
 * Every key must be registered in draft_api's ALL_TEMPLATES or create_spec 400s.
 */
export const SYSTEMDOX_DOC_TYPE_CONFIG: DocTypeConfig = {
  types: [
    {
      key: 'requirement',
      label: 'Requirement',
      aiDraft: true,
      templates: [
        { key: 'bdd', title: 'BDD requirement', description: 'User story + Given/When/Then acceptance criteria.' },
        { key: 'prd', title: 'PRD', description: 'Problem, goals, users, success metrics, scope.' },
      ],
    },
    {
      key: 'design',
      label: 'Design',
      templates: [
        { key: 'adr', title: 'ADR', description: 'Context, decision, consequences — with a diagram.' },
        { key: 'design-doc', title: 'Design doc', description: 'Overview, architecture, components, trade-offs.' },
        { key: 'rfc', title: 'RFC', description: 'Proposal for review: motivation, approach, alternatives.' },
        { key: 'test-plan', title: 'Test plan', description: 'Scope, coverage matrix, entry/exit criteria, risks.' },
      ],
    },
  ],
};

/** A document's full editor, which lives on the Build console. */
export const consoleDocumentPath = (id: string, docType: string) =>
  docType === 'design' ? `/design/${id}` : `/requirements/${id}`;

/**
 * True when create_spec rejected an unknown `template` key (HTTP 400 whose
 * message names the template). The frontend and draft_api Lambda deploy
 * independently, so a freshly-added template key can be offered by the app
 * before the backend that scaffolds it is live — we treat that as recoverable.
 */
function isTemplateRejection(err: unknown): boolean {
  const e = err as { response?: { status?: number; data?: unknown } };
  if (e?.response?.status !== 400) return false;
  const data = e.response.data;
  const message = typeof data === 'string' ? data : (data as { message?: string })?.message ?? '';
  return /template/i.test(message);
}

/**
 * Build the CaptureHost for the current tenant. `tenantSlug` is needed only
 * to deep-link into the console editor (onOpenDocument); everything else is
 * tenant-scoped server-side via the auth token.
 *
 * `onOpenDocument` is injectable because a surface that can show the document
 * where the user already is should do that: the default below leaves the origin
 * for the console editor, which is the right destination only when the caller
 * has nowhere to open it in place.
 */
export function createSystemdoxCaptureHost(opts: {
  tenantSlug: string | null;
  projectId?: string | null;
  onOpenDocument?: (id: string, docType: string) => void;
}): CaptureHost {
  return {
    refine: (request) => systemdoxRefineAdapter.refine(request),
    validate: (doc, docType) => validateRefinedDocument(doc, docType as 'requirement' | 'design'),

    async createDoc({ name, docType, template, templateId, notes }) {
      const params = {
        name,
        docType: docType as DocType | undefined,
        template: template as SpecTemplate | undefined,
        templateId,
        notes,
        // Capture stamping (PO decision): the selected project marks the new
        // artifact so Build/Document project views pick it up.
        projectId: opts.projectId ?? undefined,
      };
      try {
        const res = await specsApi.createDocument(params);
        return { id: res.draftId, docType: res.docType };
      } catch (err) {
        // If the backend hasn't yet deployed the chosen platform template, fall
        // back to the default scaffold rather than lose the document
        // (deploy-window resilience). Unknown custom templateIds already fall
        // back server-side. Any other error still surfaces to the user.
        if ((template || templateId) && isTemplateRejection(err)) {
          const res = await specsApi.createDocument({ ...params, template: undefined, templateId: undefined });
          return { id: res.draftId, docType: res.docType };
        }
        throw err;
      }
    },

    async getDoc(id) {
      const spec = await specsApi.getSpec(id);
      return {
        proposalContent: spec.proposalContent, name: spec.name,
        gitRepo: spec.gitRepo, gitPath: spec.gitPath,
      };
    },

    async updateDoc(id, patch) {
      // specsApi keys requirements by reqId; new ones get an empty id
      // (the backend assigns one).
      const { requirements, ...rest } = patch;
      await specsApi.updateSpec(id, {
        ...rest,
        ...(requirements && { requirements: requirements.map((r) => ({ reqId: '', name: r.name, content: r.content })) }),
      });
    },

    async commitDoc(id) {
      // Best-effort (ADR-033): a no-repo tenant returns committed:false and a
      // failure must never break generation — the draft still exists in the app.
      await specsApi.commitDraft(id).catch(() => undefined);
    },

    async listRecent(docType) {
      const specs = await specsApi.listSpecs({ docType: docType as DocType });
      return specs.map((s) => ({ id: s.draftId, name: s.name, status: s.status, updatedAt: s.updatedAt }));
    },

    analyzeGaps: () => aiApi.analyzeRequirementGaps(),

    onOpenDocument(id, docType) {
      if (opts.onOpenDocument) {
        opts.onOpenDocument(id, docType);
        return;
      }
      if (opts.tenantSlug) {
        window.location.href = tenantUrl(opts.tenantSlug, consoleDocumentPath(id, docType));
      }
    },

    docTypeConfig: SYSTEMDOX_DOC_TYPE_CONFIG,
  };
}
